import * as React from 'react';
import * as _ from 'underscore';

import { connect } from 'react-redux';
import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import {
  asyncStoreContainerHOC,
  AsyncStoreWrappedComponentProps
} from '../../common/react/AsyncStoreContainerHOC';
import { DeprecatedReactMenu } from '../../common/react/components/Menu';
import { HelpActions } from '../../common/react/HelpActions';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { GettingStartedNotifications } from '../../ts/services/GettingStartedNotifications';
import { HelpService } from '../../ts/services/HelpService';
import { ServerService } from '../../ts/services/ServerService';
import { Telemetry } from '../../ts/services/TelemetryService';
import { PageHeaderButtonType, PageHeaderIcons } from '../layout/PageHeader';
import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors
} from '../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../ServiceLocator';
import { AppState } from '../state/AppState';
import { MapStateToProps } from '../utils/ConnectUtils';
import { HeaderMenuButton } from './HeaderMenuButton';
import { HelpMenuPanel } from './HelpMenuPanel';

export interface HelpMenuExternalProps {
  readonly style?: any;
  readonly buttonType: PageHeaderButtonType;
  readonly telemetryService: Telemetry;
}

export interface HelpMenuPropsFromRedux {
  readonly isOfflineHelpEnabled: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
}

export interface HelpMenuPropsFromAngular {
  readonly gettingStartedNotifications: GettingStartedNotifications;
  readonly helpActions: HelpActions;
  readonly serverHelpLabel: string;
  readonly serverHelpUrl: string;
  readonly serverName: string;
  readonly supportUrl: string;
  readonly whatIsNewUrl: string;
}

export interface HelpMenuPropsFromAsyncStore {
  readonly showGettingStartedLink: boolean;
}


export type HelpMenuViewComponentProps = AsyncStoreWrappedComponentProps<
    HelpMenuAsyncStoreContainer.OwnProps, HelpMenuPropsFromAsyncStore, 'fetchShowGettingStartedProp'>;

const helpIcons: PageHeaderIcons = {
  lightIcons: {
    active: imgPath('spaces_toolbar_help_active_white.svg'),
    inactive: imgPath('spaces_toolbar_help_white.svg')
  },
  regularIcons: {
    active: imgPath('spaces_toolbar_help_active.svg'),
    inactive: imgPath('spaces_toolbar_help.svg')
  }
};

export class HelpMenuViewComponent extends React.Component<HelpMenuViewComponentProps> {

  public componentDidMount() {
    this.props.fetchShowGettingStartedProp();
  }

  public componentDidUpdate(prevProps: HelpMenuViewComponentProps) {

    // To conserve API calls, fetch the data only when the user's admin status changes (e.g. site switch).
    // Update show/hide Getting Started menu item when user's admin status changes (e.g., site switch),
    // because this is when the flag is likely to change.
    //
    // Note (drbr 2019-01-24): This seems like the wrong thing to watch on; I feel like we might
    // want to watch the current site ID too, but we were watching only the admin status
    // in the old TopBar so I'm just porting it over the same way.
    if (this.props.isSessionSiteOrServerAdmin !== prevProps.isSessionSiteOrServerAdmin) {
      this.props.fetchShowGettingStartedProp();
    }
  }

  private itemSelected = () => {
    const ref = this.refs['menu'] as DeprecatedReactMenu;
    if (ref) {
      ref.closeMenu();
    }
    window.removeEventListener('blur', this.onWindowBlur);
  }

  public render(): JSX.Element {
    const icons = (this.props.buttonType === 'light') ? helpIcons.lightIcons : helpIcons.regularIcons;

    return DeprecatedReactMenu.element({
      ref: 'menu',
      tbTestId: 'help-menu',
      style: this.props.style,
      alignPanelRight: true,
      focusOnEscape: true,

      menuButtonFactory: (props?: HeaderMenuButton.Props) => {
        props.iconPath = props.hovered || props.menuOpen ? icons.active : icons.inactive;
        props.tabIndex = 0;
        props.ariaPopup = 'menu';
        props.altText = Messages.helpAltText();
        props.ariaLabel = Messages.helpAltText();

        return HeaderMenuButton.element(props);
      },

      menuPanelFactory: (helpMenuPanelProps?: HelpMenuPanel.Props) => {
        helpMenuPanelProps.itemSelected = this.itemSelected;

        // Copy properties from HelpMenu to HelpMenuPanel
        _.extend(helpMenuPanelProps, this.props);

        return React.createElement(HelpMenuPanel, helpMenuPanelProps);
      },
      onBeforeMenuOpen: () => {
        this.props.telemetryService.sendEvent<PageHeaderButtonClickEvent>({
          type: TelemetryEvents.PageHeaderButtonClick,
          button: 'help-menu',
          action: 'open'
        });
        window.addEventListener('blur', this.onWindowBlur);
        return Q.when();
      }
    });
  }

  private onWindowBlur = () => {
    this.itemSelected();
  }
}


export namespace HelpMenuAsyncStoreContainer {

  async function fetchDataFunction(props: HelpMenuPropsFromAngular): Promise<HelpMenuPropsFromAsyncStore> {
    const showGettingStartedLink = await props.gettingStartedNotifications.shouldShowGettingStartedLink();
    return { showGettingStartedLink };
  }

  export type OwnProps = HelpMenuExternalProps & HelpMenuPropsFromRedux & HelpMenuPropsFromAngular;

  export const HOC = asyncStoreContainerHOC<OwnProps, HelpMenuPropsFromAsyncStore, 'fetchShowGettingStartedProp'>({
    fetchDataFunction,
    internalPropName: 'fetchShowGettingStartedProp'
  });
}


export namespace HelpMenuAngularProvider {

  interface AngularServices {
    gettingStartedNotifications: GettingStartedNotifications;
    helpActions: HelpActions;
    helpService: HelpService;
    serverService: ServerService;
  }

  function getAngularServicesWithServiceLocator() {
    const gettingStartedNotifications = ServiceLocator.get<GettingStartedNotifications>('GettingStartedNotifications');
    const helpService = ServiceLocator.get<HelpService>('HelpService');
    const serverService = ServiceLocator.get<ServerService>('ServerService');

    return {
      gettingStartedNotifications,
      helpActions: new HelpActions(helpService, serverService, gettingStartedNotifications),
      helpService,
      serverService,
    };
  }

  function selectPropsFromAngularServices(angularResources: AngularServices): HelpMenuPropsFromAngular {
    const { gettingStartedNotifications, helpActions, helpService, serverService } = angularResources;
    return {
      gettingStartedNotifications,
      helpActions,
      serverHelpLabel: helpService.getHelpLinkText(),
      serverHelpUrl: helpService.getServerHelpUrlByCategory(),
      supportUrl: helpService.getGeneralSupportUrl(),
      whatIsNewUrl: helpService.getWhatIsNewUrl(),
      serverName: serverService.serverName(),
    };
  }

  export const HOC = angularProviderHOC<HelpMenuExternalProps & HelpMenuPropsFromRedux, HelpMenuPropsFromAngular, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}


export namespace ReduxConnectedHelpMenu {

  const mapStateToProps: MapStateToProps<HelpMenuPropsFromRedux> = (state: AppState) => ({
    isOfflineHelpEnabled: SessionServerSelectors.offlineHelpEnabled(state),
    isSessionSiteOrServerAdmin: CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(state),
  });

  export const HOC = connect(mapStateToProps);
}


/*
 * Three levels of higher-order provider components, to gather props from three different sources:
 *   - Connected Redux component (props from Redux)
 *   - AngularProvider (props from Angular services)
 *   - Async store HOC (props from other API calls)
 */
export const HelpMenu: React.ComponentType<HelpMenuExternalProps> =
  ReduxConnectedHelpMenu.HOC(
    HelpMenuAngularProvider.HOC(
      HelpMenuAsyncStoreContainer.HOC(
        HelpMenuViewComponent
      )
    )
  );
